import { Dialog, GridHookForm } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useModal } from '@/contexts/modal'
import { useState } from 'react'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { renderFileItem } from '@/utils/function'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'

export interface ResolveNegotiationProps extends ModalProps {
    negotiationId: string
    onSubmit: (data: any) => Promise<void>
}

interface ResolveFormData {
    resolution_notes: string
    final_value?: number
    attachments: File[]
}

const ResolveNegotiation: React.FC<ResolveNegotiationProps> = ({
    onClose,
    open,
    onSubmit,
}) => {
    const { t } = useTranslation()
    const { closeModal } = useModal()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_NEGOTIATION,
    })

    const formControl = useForm<ResolveFormData>({
        defaultValues: {
            resolution_notes: '',
            final_value: undefined,
            attachments: [],
        },
    })

    const handleSubmit = async (values: ResolveFormData) => {
        setIsSubmitting(true)
        try {
            let attachments: Attachment[] = []
            if (values.attachments && values.attachments.length > 0) {
                const attachmentUploadeds = await uploadAsync(
                    values.attachments
                )
                attachments = attachmentUploadeds
                    ?.filter((attachment) => attachment.status === 'fulfilled')
                    .map((attachment) => attachment.value) as Attachment[]
            }

            await onSubmit({
                resolution_notes: values.resolution_notes,
                final_value: values.final_value,
                attachments,
            })
            closeModal()
        } catch (error) {
            console.error('Error resolving negotiation:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onDelete = (file: File | Attachment) => {
        const newAttachments = formControl
            .getValues('attachments')
            .filter((f: File | Attachment) => f !== file)
        formControl.setValue('attachments', newAttachments)
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('resolve-negotiation')}>
            <GridHookForm
                formControl={formControl}
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: closeModal,
                    },
                    {
                        title: t('resolve-negotiation'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isSubmitting,
                        disabled: isSubmitting,
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'resolution_notes',
                            type: 'textarea',
                            label: t('resolution-notes'),
                            placeholder: t('enter-resolution-notes'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            multiple: true,
                            label: t('attachments'),
                            placeholder: t('attachments'),
                            renderFileItem: (file: File) => {
                                return renderFileItem(file, onDelete)
                            },
                        },
                        className: 'col-span-full',
                    },
                ]}
            />
        </Dialog>
    )
}

export default ResolveNegotiation
